/**
 * ENTITLEMENTS domain state — which products the account owns.
 *
 * ============================================================================
 * CLIENT-SIDE ONLY, THEREFORE NOT A SECURITY BOUNDARY. Every flag here can be
 * flipped from devtools, and `purchase()` charges nothing. Gating built on this
 * store is a UX affordance, not access control.
 *
 * Real gating requires productsApi.getEntitlements() to be server-authoritative
 * and the paid features themselves to be enforced server-side. Phase 4.
 * ============================================================================
 *
 * `studio`, `composerPlus` and `reseller` are retained even though the current
 * UI only reads some of them: Phase 4 uses all three for real feature gating,
 * and dropping them now would mean re-deriving the mapping later.
 */
import { create } from 'zustand';
import { EMPTY_ENTITLEMENT, PRODUCT_ENTITLEMENT, productsApi } from '../api/productsApi';
import { USE_MOCK_API } from '../config';
import type { BonusClaimResult, Entitlement, Product, ProductId } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';
import { useBonusClaimsStore } from './bonusClaimsStore';

interface EntitlementsState extends Entitlement {
  /** Bonus pack ids already claimed. */
  claimedBonuses: string[];

  /**
   * Where the entitlement READ has got to. See stores/loadState.ts.
   *
   * ==========================================================================
   * THE STATE THAT MATTERS MOST TO THE GATING, AND THE ONE THAT CANNOT FIX IT. The header
   * above says why: these flags are client-side, so nothing here is access control. But
   * the read state is still the difference between two very different situations —
   * "the server says this account owns nothing" and "we could not ask the server" — and
   * a paywall built on the second is a paywall that appears during an outage.
   *
   * The correct behaviour for a real deployment is that a failed entitlement read leaves
   * the previous answer standing and the SERVER refuses the paid operation, not the
   * client. `loadEntitlements` does the first half; only a backend can do the second.
   * ==========================================================================
   */
  load: LoadState;

  /**
   * Buys a one-time product. Returns it so the caller can name it in a toast.
   *
   * ==========================================================================
   * THE PURCHASE NO LONGER GRANTS THE ENTITLEMENT. It used to: `set({ [flag]: true })`
   * immediately after checkout resolved, which made the browser the authority on what the
   * account owns — "the UI thinks the purchase succeeded, therefore the feature is unlocked".
   * A real deployment fulfils asynchronously, by webhook, and checkout resolving is not the
   * same event as fulfilment completing.
   *
   * SO THE ORDER IS NOW: initiate at the boundary → wait for it → RE-READ from
   * `GET /entitlements`, which productsApi calls "the ONLY authority the UI should trust for
   * gating" → the UI updates from that answer. `hydrate` applies it; nothing sets a flag
   * from a purchase.
   *
   * WHAT THAT MEANS TODAY, HONESTLY: `getEntitlements()` returns the empty set, so against a
   * real backend a purchase would correctly leave the account owning nothing until the
   * endpoint is implemented. That is why the re-read is skipped in mock mode — see the
   * implementation, where the reason and its consequence are both stated. The client
   * granting the flag itself is the one thing that is no longer an option.
   * ==========================================================================
   */
  purchase: (productId: ProductId) => Promise<Product>;
  /**
   * Buys the Commercial License & Creator Toolkits and stamps the purchase date.
   *
   * Same authority rule as `purchase`. `commercialPurchaseDate` is the exception and is
   * still set locally: it is a display string the caller formats from the clock, `Entitlement`
   * carries it but `getEntitlements()` has no purchase record to derive it from, and a
   * re-read that blanked it would lose the date the licence page prints.
   */
  purchaseCommercial: (purchaseDate: string) => Promise<void>;
  claimBonus: (bonusId: string) => Promise<BonusClaimResult>;
  /**
   * Fetches the account's entitlements through `productsApi.getEntitlements()`.
   *
   * THE ONE READ IN THE APP THAT WAS ALREADY DOCUMENTED AS THE ANSWER, and still had no
   * caller: this store's header says "Real gating requires productsApi.getEntitlements()
   * to be server-authoritative", and the function existed, and nothing invoked it. Now
   * there is a path — so the sentence describes a seam rather than an intention.
   *
   * THE FETCHED SET REPLACES THE ENTITLEMENT FLAGS. Claimed bonus ids remain in their
   * dedicated store and are refreshed independently.
   *
   * Never rejects; failures land in `load.error`, and the previous answer stands.
   */
  loadEntitlements: () => Promise<void>;
  /** Replaces all entitlements, e.g. from a future server fetch. */
  hydrate: (entitlement: Entitlement) => void;
  /** Resets to a fresh account. Used on logout and by tests. */
  reset: () => void;
}

/**
 * Brings the entitlement flags up to date after a purchase, from the authoritative read.
 *
 * ============================================================================
 * THE TWO MODES, AND NEITHER OF THEM IS THE CLIENT DECIDING.
 *
 * WITH A BACKEND (`USE_MOCK_API` false) it re-reads `GET /entitlements` and applies whatever
 * comes back. If fulfilment has not completed, the flag stays off — which is correct, and is
 * the behaviour the brief asks for: the client must not permanently grant an entitlement
 * because the UI thinks a purchase succeeded. `loadEntitlements` records the read's own state
 * and never rejects, so a failed re-read leaves the previous answer standing rather than
 * revoking access.
 *
 * IN MOCK MODE the flag is set locally, and this is the one place in this change where the
 * client writes an entitlement. The reason is not convenience, it is that the alternative is
 * worse in both directions:
 *
 *   `getEntitlements()` returns EMPTY_ENTITLEMENT unconditionally — it is a fixture with no
 *   purchase record behind it. Calling it after a purchase would therefore REVOKE everything
 *   the session had unlocked, so buying Lyrixa Studio would turn the Studio off. That is not
 *   a more honest answer, it is a different wrong one; and it would break the demo path the
 *   whole prototype is navigated through.
 *
 * The dishonesty this build has to own is that a mock purchase unlocks a feature for free,
 * and that is stated at the top of this file, in api/productsApi.ts and on screen in the
 * Products page's notices. What is now impossible is the client asserting an entitlement
 * against a real server that has not granted it.
 *
 * `USE_MOCK_API` is the same switch hooks/useAppData gates the boot reads on, for the same
 * reason and with the same consequence: point the app at a backend and the seam is live.
 * ============================================================================
 */
async function grantFromAuthority(
  productId: ProductId,
  set: (partial: Partial<EntitlementsState>) => void,
  get: () => EntitlementsState
): Promise<void> {
  if (USE_MOCK_API) {
    set({ [PRODUCT_ENTITLEMENT[productId]]: true } as Partial<EntitlementsState>);
    return;
  }
  await get().loadEntitlements();
}

export const useEntitlementsStore = create<EntitlementsState>((set, get) => ({
  ...EMPTY_ENTITLEMENT,
  claimedBonuses: [],
  load: IDLE_LOAD,

  async purchase(productId) {
    // Step 1: initiate at the boundary and wait for it. A rejection propagates — the caller
    // toasts the failure — and nothing has been granted, because nothing grants here.
    const product = await productsApi.purchaseProduct(productId);
    // Step 2 and 3: re-read the authority, and update from its answer.
    await grantFromAuthority(productId, set, get);
    return product;
  },

  async purchaseCommercial(purchaseDate) {
    await productsApi.purchaseCommercial();
    // The ENTITLEMENT comes from the authority; the date is local (see the interface note).
    await grantFromAuthority('commercial', set, get);
    /*
      WRITTEN AFTER THE RE-READ, AND THE ORDER IS LOAD-BEARING. `commercialPurchaseDate` is
      part of `Entitlement`, so `hydrate` replaces it along with the flags — and
      `getEntitlements()` answers `EMPTY_ENTITLEMENT`, whose date is the empty string. Setting
      it first therefore had it wiped by the very read that was meant to confirm the purchase,
      losing the date the licence page prints. Last write wins, so it goes last.
    */
    set({ commercialPurchaseDate: purchaseDate });
  },

  async claimBonus(bonusId) {
    const result = await useBonusClaimsStore.getState().claimBonus(bonusId);
    set({ claimedBonuses: useBonusClaimsStore.getState().claimedBonuses });
    return result;
  },

  async loadEntitlements() {
    set({ load: LOADING_LOAD });
    try {
      const entitlement = await productsApi.getEntitlements();
      get().hydrate(entitlement);
      set({ load: READY_LOAD });
    } catch (err) {
      // The flags stand. A failed read must not revoke access the account has — see the
      // note on `load` about paywalls that appear during an outage.
      set({ load: failedLoad(err) });
    }
  },

  hydrate(entitlement) {
    set({ ...entitlement });
  },

  reset() {
    useBonusClaimsStore.getState().reset();
    set({ ...EMPTY_ENTITLEMENT, claimedBonuses: [], load: IDLE_LOAD });
  },
}));
